import React, { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';

const API_BASE = '';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

interface BudgetVsActual {
  budget: { budget_id: string; total_budget: number; status: string; notes?: string };
  total_budget: number;
  total_billed: number;
  total_wip: number;
  total_spent: number;
  remaining: number;
  percentage_used: number;
  exceeded_thresholds: Array<{ percentage: number }>;
}

interface MatterBudgetCardProps {
  matterId: string;
}

const MatterBudgetCard: React.FC<MatterBudgetCardProps> = ({ matterId }) => {
  const { keycloak } = useKeycloak();
  const [data, setData] = useState<BudgetVsActual | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchBudget = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/v1/billing/matter-budgets/matter/${matterId}/vs-actual`, {
        headers: getHeaders(),
      });
      if (res.ok) {
        const json = await res.json();
        setData(json.data);
      } else {
        setData(null);
      }
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [matterId, getHeaders]);

  useEffect(() => { fetchBudget(); }, [fetchBudget]);

  const handleCreateBudget = async () => {
    const amount = parseFloat(budgetAmount);
    if (!amount || amount <= 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/billing/matter-budgets`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          matter_id: matterId,
          total_budget: amount,
          alert_thresholds: [
            { percentage: 75, notify_users: [] },
            { percentage: 90, notify_users: [] },
          ],
        }),
      });
      if (res.ok) {
        setShowSetup(false);
        setBudgetAmount('');
        await fetchBudget();
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  // No budget set — show setup option
  if (!data) {
    if (showSetup) {
      return (
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Set Matter Budget</h4>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="100"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder="Budget amount"
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              autoFocus
            />
            <button
              onClick={handleCreateBudget}
              disabled={saving || !budgetAmount}
              className="px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setShowSetup(false)}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        onClick={() => setShowSetup(true)}
        className="w-full text-left bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400 hover:border-lex-accent hover:text-lex-accent transition-colors"
      >
        + Set matter budget
      </button>
    );
  }

  // Budget exists — show progress
  const pct = Math.min(100, data.percentage_used * 100);
  const barColor =
    pct >= 100 ? 'bg-red-500' :
    pct >= 90 ? 'bg-orange-500' :
    pct >= 75 ? 'bg-yellow-500' : 'bg-green-500';

  const statusBadge =
    data.budget.status === 'exceeded' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300' :
    data.budget.status === 'closed' ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400' :
    'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white">Matter Budget</h4>
        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${statusBadge}`}>
          {data.budget.status.charAt(0).toUpperCase() + data.budget.status.slice(1)}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
        <div className={`h-3 rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="text-right text-xs font-medium text-gray-600 dark:text-gray-400 mb-3">
        {pct.toFixed(1)}% used
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Budget</div>
          <div className="font-semibold text-gray-900 dark:text-white">{formatCurrency(data.total_budget)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Billed</div>
          <div className="font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(data.total_billed)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">WIP</div>
          <div className="font-semibold text-amber-600 dark:text-amber-400">{formatCurrency(data.total_wip)}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">Remaining</div>
          <div className={`font-semibold ${data.remaining >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {formatCurrency(data.remaining)}
          </div>
        </div>
      </div>

      {/* Threshold warnings */}
      {data.exceeded_thresholds.length > 0 && (
        <div className="mt-3 p-2 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800 rounded text-xs text-orange-700 dark:text-orange-300">
          Budget alert: {Math.max(...data.exceeded_thresholds.map((t) => t.percentage))}% threshold exceeded
        </div>
      )}
    </div>
  );
};

export default MatterBudgetCard;
